import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, auth } from '../lib/api';
import { track } from '../lib/analytics';
import AuthBrand from '../components/AuthBrand';
import InlineError from '../components/InlineError';
import PasswordInput from '../components/PasswordInput';

interface Preview {
  valid: boolean;
  /** The account already has a password — the invite was already used, so
   * the applicant belongs on sign-in, not the set-password form. */
  alreadyActivated?: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Magic-link landing (/onboarding?token=…) — the portal end of the
 * HubSpot → Globus → n8n pipeline. Preview greets the applicant from their
 * Globus record; setting a password creates their account and signs them in.
 */
export default function OnboardingPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview>();
  const [error, setError] = useState<string>();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This invite link is missing its token. Please use the link from your email.');
      return;
    }
    api<Preview>('/onboarding/validate-token', { method: 'POST', body: JSON.stringify({ token }) })
      .then((p) => {
        if (p.alreadyActivated) {
          // Invite already used — the password is set. Send them to sign-in
          // rather than re-showing (and letting them overwrite) their password.
          track('onboarding_already_activated', {});
          navigate('/sign-in', {
            replace: true,
            state: { info: 'Your account is already set up. Please sign in with your password.' },
          });
          return;
        }
        setPreview(p);
      })
      .catch((e) => {
        setError(e.message);
        track('onboarding_token_invalid', { message: (e as Error).message });
      });
  }, [token, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const done = await api<{ success: boolean; email: string }>('/onboarding/complete', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      track('onboarding_completed', {});
      // Sign straight in with the new credentials — no second form.
      const login = await api<{ accessToken: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: done.email, password }),
      });
      auth.setToken(login.accessToken);
      navigate('/dashboard');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <AuthBrand
        title={`Welcome${preview?.firstName ? `, ${preview.firstName}` : ''}!`}
        subtitle={preview?.email ? `Set a password for ${preview.email} to activate your portal account.` : 'Activating your invitation…'}
      />
      {error && !preview && <div className="auth-card" role="alert"><p>{error}</p></div>}
      {preview && (
        <form className="auth-card" onSubmit={onSubmit}>
          <label>
            Choose a password (10+ characters)
            <PasswordInput value={password} minLength={10} required autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label>
            Confirm password
            <PasswordInput value={confirm} minLength={10} required autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)} />
          </label>
          {error && <InlineError message={error} />}
          <button type="submit" disabled={busy}>{busy ? 'Creating your account…' : 'Activate account'}</button>
        </form>
      )}
    </main>
  );
}
